import { forwardRef, type ButtonHTMLAttributes } from 'react';

/**
 * The shared Button: the platform's block/form action (n39, 04 #39). Six roles, every
 * fill/edge/text a global.css theme-sheet token, plus the shared anatomy tokens
 * (--btn-radius, --btn-font-weight via .control-weight, --btn-shadow/-hover):
 * - primary:   the brand fill; the one main action of a form or dialog.
 * - secondary: the accent fill; a co-equal or additive action (Add line, Refresh).
 * - tertiary:  bordered-quiet (the PillButton-neutral look at Button scale); Cancel and
 *              other visible-but-calm actions. Rides .control-glass, so the quiet-role
 *              glass treatment (--btn-glass-alpha/-filter) applies here when enabled.
 * - ghost:     borderless-quiet; truly in-context actions. Kept alongside tertiary by
 *              owner ruling (n39 fork A): both exist, no rename churn.
 * - positive:  green; money-in / confirming actions (Record payment, Confirm). Solid in
 *              light, the green family wash in dark (the danger asymmetry, owner-ruled).
 * - danger:    red; destructive finals. The code name stays `danger` (owner ruling): it
 *              is the destructive role of the n39 set.
 * Keep PillButton for in-row/in-card actions; keep SegmentedSelect for exclusive choice.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'positive' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--color-primary)] text-[var(--btn-primary-text)] ring-1 ring-inset ring-[var(--btn-primary-edge)] hover:opacity-90 active:opacity-80',
  secondary:
    'bg-[var(--btn-secondary-bg)] text-[var(--btn-secondary-text)] ring-1 ring-inset ring-[var(--btn-secondary-edge)] hover:opacity-90 active:opacity-80',
  tertiary:
    'control-glass text-[var(--btn-tertiary-text)] ring-1 ring-inset ring-[var(--btn-tertiary-border)] hover:bg-[var(--btn-tertiary-hover-bg)] active:bg-[rgb(var(--edge-rgb)/0.10)]',
  ghost:
    'bg-transparent text-[var(--color-text)] hover:bg-[rgb(var(--edge-rgb)/0.05)] active:bg-[rgb(var(--edge-rgb)/0.10)]',
  positive:
    'bg-[var(--btn-positive-bg)] text-[var(--btn-positive-text)] ring-1 ring-inset ring-[var(--btn-positive-edge)] hover:bg-[var(--btn-positive-bg-hover)] active:bg-[var(--btn-positive-bg-active)]',
  danger:
    'bg-[var(--btn-danger-bg)] text-[var(--btn-danger-text)] ring-1 ring-inset ring-[var(--btn-danger-edge)] hover:bg-[var(--btn-danger-bg-hover)] active:bg-[var(--btn-danger-bg-active)]',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

/**
 * The full visual class string for a Button role + size, WITHOUT the state classes
 * (loading / disabled) the component adds. Exported so an anchor or router Link can wear
 * exactly the Button treatment when its correct element is <a> (an external launch link,
 * a deep link) — one source of truth, zero drift (n39 layer 2b). The component itself
 * composes through this same builder.
 */
export function buttonClasses(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  className = ''
): string {
  return [
    // Geometry + depth read the n39 anatomy tokens. The shadow utilities set
    // --tw-shadow, which composes WITH the ring box-shadows; the token default is the
    // empty shadow (0 0 #0000), so rest and hover render exactly as before.
    'relative inline-flex items-center justify-center gap-2 rounded-[var(--btn-radius)] control-weight transition',
    'shadow-[var(--btn-shadow)] hover:shadow-[var(--btn-shadow-hover)]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-accent)]',
    variantClasses[variant],
    sizeClasses[size],
    className,
  ].join(' ');
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, disabled, className = '', children, ...rest },
  ref
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      disabled={isDisabled}
      className={[
        buttonClasses(variant, size, className),
        loading ? 'opacity-100 cursor-wait' : 'disabled:opacity-50 disabled:cursor-not-allowed',
      ].join(' ')}
      {...rest}
    >
      {loading ? (
        <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <Spinner />
        </span>
      ) : null}
      <span className={['inline-flex items-center gap-2', loading ? 'invisible' : ''].join(' ')}>
        {children}
      </span>
    </button>
  );
});

function Spinner(): JSX.Element {
  return (
    <svg
      className="animate-spin h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path
        d="M4 12a8 8 0 018-8"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}
